import React, { useEffect, useMemo, useState } from 'react';
import useAxios from 'axios-hooks';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { alpha, makeStyles, useTheme } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import ImageList from '@material-ui/core/ImageList';
import { isWidthUp, useMediaQuery, WithWidthProps } from '@material-ui/core';
import withWidth from '@material-ui/core/withWidth';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import InputBase from '@material-ui/core/InputBase';
import SearchIcon from '@material-ui/icons/Search';

import { IProduct, IStaticInfos, IUser } from '../types';
import { AppState } from '../redux';
import { getAuthenticationState } from '../redux/selectors';
import { ShopMode } from '../redux/types';
import ProductComponent from '../components/ProductComponent';

import homeDelivery from '../assets/images/home-delivery.png';

const useStyles = makeStyles((theme) => ({
  topBox: {
    overflow: 'hidden',
    height: '280px',
  },
  topImage: {
    position: 'relative',
    display: 'flex',
    '& .image': { width: '100%' },
    '& .text': {
      top: '0',
      left: '0',
      color: '#fff',
      position: 'absolute',
      fontSize: '25px',
      fontWeight: '600',
      letterSpacing: '0.5px',
      backgroundColor: '#263f427b',
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '5% 10%',
      lineHeight: 2.5,
      flexDirection: 'column',
      [theme.breakpoints.down('sm')]: {
        padding: '5%',
      },
      '& .descriptionBox': {
        display: 'flex',
        alignItems: 'center',
        fontSize: 30,
        fontWeight: '500',
        fontStyle: 'normal',
        lineHeight: 46,
        letterSpacing: 0,
        textAlign: 'center',
        height: '100%',
        '& img': {
          maxHeight: '100%',
        },
        '& p': {
          padding: '0 5%',
        },
      },
    },
  },
  loadingBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    backgroundColor: theme.palette.secondary.main,
    color: '#fff',
  },
  productsGridTitle: {
    padding: theme.spacing(3),
    paddingBottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    [theme.breakpoints.down('xs')]: {
      flexWrap: 'wrap',
    },
  },
  search: {
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(theme.palette.common.white, 0.15),
    '&:hover': {
      backgroundColor: alpha(theme.palette.common.white, 0.25),
    },
    marginLeft: 0,
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(1),
      width: 'auto',
    },
  },
  searchIcon: {
    padding: theme.spacing(0, 2),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  inputRoot: {
    color: 'inherit',
  },
  inputInput: {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)}px)`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '12ch',
      '&:focus': {
        width: '20ch',
      },
    },
  },
  productsGrid: {
    backgroundColor: theme.palette.secondary.main,
    padding: theme.spacing(2),
  },
}));

interface Props extends WithWidthProps {
  user: IUser | null;
  shopMode: ShopMode | null;
}

const Ordina: React.FC<Props> = ({ width = 'md', user, shopMode }) => {
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const theme = useTheme();
  const location = useLocation();
  const mediaQueryMatchesXs = useMediaQuery(theme.breakpoints.down('xs'));

  const [{ data: products = [], loading, error }] = useAxios<IProduct[]>(
    {
      url: 'products',
      params: {
        filters: {
          available: true,
        },
        sort: ['name:asc'],
        pagination: {
          page: 1,
          pageSize: 200,
        },
      },
    },
    {
      useCache: false,
    }
  );
  const [{ data: staticInfos }] = useAxios<IStaticInfos>('static-info');
  const [searchTerm, setSearchTerm] = useState<string>('');

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  }, [enqueueSnackbar, error]);

  const filteredProducts = useMemo(() => {
    let filtered = products.filter((product) => {
      if (shopMode === ShopMode.DirectSale) {
        return product.directSale === true;
      } else {
        return true;
      }
    });

    if (searchTerm) {
      filtered = filtered.filter((product) => product.name.toLowerCase().includes(searchTerm.toLowerCase()));
    }

    return filtered;
  }, [products, shopMode, searchTerm]);

  useEffect(() => {
    if (!loading && filteredProducts.length > 0 && location.hash) {
      const elementId = location.hash.slice(1);
      const element = document.getElementById(elementId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [loading, filteredProducts, location.hash]);

  const getImageListCols = () => {
    if (isWidthUp('xl', width)) {
      return 5;
    }

    if (isWidthUp('md', width)) {
      return 4;
    }

    if (isWidthUp('sm', width)) {
      return 3;
    }

    if (isWidthUp('xs', width)) {
      return 1;
    }

    return 1;
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  return (
    <Container disableGutters>
      {staticInfos && (
        <Box className={classes.topImage}>
          <img className="image" src={staticInfos.ordinaTopImage.url} alt="" />
          <div className="text">
            <Typography className="title" variant={'h6'}>
              Ordina online, ricevi a casa
            </Typography>
            {!mediaQueryMatchesXs && (
              <div className="descriptionBox">
                <img src={homeDelivery} alt="" />
                <Typography className="description">
                  Il nostro negozio virtuale ti consente di prenotare i prodotti e di riempire il carrello comodamente
                  da casa. Il tuo ordine verrà evaso all’istante e consegnato nella data disponibile indicata.
                </Typography>
              </div>
            )}
          </div>
        </Box>
      )}
      <Box className={classes.productsGridTitle} bgcolor="secondary.main" color="#fff">
        <Typography variant={'h6'}>I nostri prodotti</Typography>
        <div className={classes.search}>
          <div className={classes.searchIcon}>
            <SearchIcon />
          </div>
          <InputBase
            placeholder="Cerca…"
            classes={{
              root: classes.inputRoot,
              input: classes.inputInput,
            }}
            inputProps={{ 'aria-label': 'search' }}
            onChange={handleSearch}
          />
        </div>
      </Box>
      {loading && (
        <Box className={classes.loadingBox}>
          <CircularProgress color="inherit" />
        </Box>
      )}
      {!loading && filteredProducts && (
        <ImageList
          className={classes.productsGrid}
          rowHeight={'auto'}
          gap={20}
          cols={getImageListCols()}
          color={'secondary'}
          style={{ margin: '-1px 0 0 0' }}
        >
          {filteredProducts.map((product) => (
            <ProductComponent
              key={product.id}
              id={`product-${product.id}`}
              product={product}
              showPrice={!!user}
              shopMode={shopMode}
              autoOpenModal={location.hash === `#product-${product.id}`}
            />
          ))}
        </ImageList>
      )}
      {shopMode === ShopMode.DirectSale && (
        <Box sx={{ padding: 2, textAlign: 'center' }}>
          <Typography variant="body2">
            I prezzi potrebbero differire in quanto alcuni prodotti potrebbero richiedere imballaggi specifici.
          </Typography>
        </Box>
      )}
    </Container>
  );
};

const mapStateToProps = (state: AppState) => ({
  user: getAuthenticationState(state).user,
  shopMode: state.shopMode.mode,
});
export default connect(mapStateToProps)(withWidth()(Ordina));
